/**
 * Jul-2026 (User-Wunsch) — Öffentliches Handy-Portal „Artikel checken".
 * Aufruf über den QR auf der Warenwirtschaft-Seite (/artikel-check/{token}).
 * Barcode scannen (Kamera) oder EAN/SKU/Name eingeben → alle Artikeldaten
 * inkl. Bestand ALLER Standorte der Organisation (standortübergreifend).
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Camera, Search, Loader2, PackageSearch, MapPin, AlertTriangle } from "lucide-react";
import EanCameraScanner from "../components/wms/EanCameraScanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtEur = (v) => (v || v === 0 ? Number(v).toLocaleString("de-DE", { style: "currency", currency: "EUR" }) : "—");

export default function ArticleCheckPage() {
    const { token } = useParams();
    const [searchParams] = useSearchParams();
    const [info, setInfo] = useState(null);
    const [error, setError] = useState("");
    const [q, setQ] = useState("");
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(false);
    const [scanOpen, setScanOpen] = useState(false);

    useEffect(() => {
        axios.get(`${API}/public/article-check/${token}`)
            .then((r) => setInfo(r.data))
            .catch((e) => setError(e?.response?.data?.detail || "Link ungültig oder abgelaufen."));
    }, [token]);

    const search = useCallback(async (query) => {
        const val = String(query || "").trim();
        if (val.length < 2) { setItems(null); return; }
        setLoading(true);
        try {
            const r = await axios.get(`${API}/public/article-check/${token}/lookup`, { params: { q: val } });
            setItems(r.data?.items || []);
        } catch (e) {
            setError(e?.response?.data?.detail || "Suche fehlgeschlagen.");
        } finally {
            setLoading(false);
        }
    }, [token]);

    // Vorbefüllte Suche via ?q= (z. B. aus Scanner-Apps)
    useEffect(() => {
        const initial = searchParams.get("q");
        if (initial) { setQ(initial); search(initial); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onDetected = (code) => {
        setScanOpen(false);
        setQ(code);
        search(code);
    };

    if (error) {
        return (
            <div className="min-h-screen bg-[#111827] text-white flex items-center justify-center p-6">
                <div className="text-center" data-testid="article-check-error">
                    <AlertTriangle className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                    <p className="font-bold">{error}</p>
                    <p className="text-sm text-gray-400 mt-2">Bitte am PC in der Warenwirtschaft einen neuen QR-Code anzeigen.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100" data-testid="article-check-page">
            <div className="bg-[#111827] text-white px-4 py-4">
                <h1 className="text-lg font-black tracking-tight inline-flex items-center gap-2">
                    <PackageSearch className="w-5 h-5 text-[#FFCC00]" /> Artikel checken
                </h1>
                <p className="text-xs text-gray-400 mt-0.5">{info?.dealer_label || "…"} · Bestand aller Standorte</p>
            </div>

            <div className="p-4 max-w-xl mx-auto space-y-4">
                <div className="flex gap-2">
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && search(q)}
                            placeholder="EAN / SKU / Artikelname…"
                            className="w-full pl-9 pr-3 py-3 rounded-sm border border-gray-300 text-sm bg-white"
                            data-testid="article-check-input"
                        />
                    </div>
                    <button type="button" onClick={() => search(q)} className="px-4 py-3 rounded-sm bg-[#111827] text-white text-sm font-bold" data-testid="article-check-search-btn">
                        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Suchen"}
                    </button>
                </div>
                <button type="button" onClick={() => setScanOpen(true)} className="w-full py-3 rounded-sm bg-[#FFCC00] text-[#111827] font-black text-sm inline-flex items-center justify-center gap-2" data-testid="article-check-scan-btn">
                    <Camera className="w-4 h-4" /> Barcode mit Kamera scannen
                </button>

                {items && items.length === 0 && (
                    <div className="text-center text-sm text-gray-500 py-6" data-testid="article-check-empty">
                        Kein Artikel gefunden. Ist der Barcode am Artikel angelegt?
                    </div>
                )}

                {(items || []).map((a, i) => (
                    <div key={i} className="bg-white rounded-sm border border-gray-200 p-4" data-testid={`article-check-result-${i}`}>
                        <div className="font-black text-[#111827]">{a.name}</div>
                        <div className="text-xs text-gray-500 mt-1 space-x-2">
                            {a.sku && <span>SKU <b className="font-mono-num">{a.sku}</b></span>}
                            {a.ean && <span>EAN <b className="font-mono-num">{a.ean}</b></span>}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5 space-x-2">
                            {a.manufacturer && <span>{a.manufacturer}</span>}
                            {a.manufacturer_part_number && <span>Teile-Nr. {a.manufacturer_part_number}</span>}
                            {a.category && <span className="uppercase">{a.category}</span>}
                        </div>
                        {a.description && <p className="text-xs text-gray-600 mt-1.5 line-clamp-3">{a.description}</p>}
                        <div className="flex items-center justify-between mt-3">
                            <span className="text-sm font-black">{fmtEur(a.sell_price_gross)} <span className="text-[10px] text-gray-400 font-normal">VK brutto</span></span>
                            <span className={`text-sm font-black ${a.total_stock > 0 ? "text-emerald-600" : "text-red-600"}`} data-testid={`article-check-total-stock-${i}`}>
                                Gesamt: {a.total_stock} {a.unit || "Stk"}
                            </span>
                        </div>
                        <div className="mt-3 border-t border-gray-100 pt-2 space-y-1.5">
                            <div className="text-[10px] uppercase tracking-wider font-black text-gray-400">Bestand je Standort</div>
                            {a.sites.map((s, j) => (
                                <div key={j} className="flex items-center justify-between text-sm" data-testid={`article-check-site-${i}-${j}`}>
                                    <span className="inline-flex items-center gap-1.5 min-w-0">
                                        <MapPin className={`w-3.5 h-3.5 shrink-0 ${s.is_home ? "text-[#FFCC00]" : "text-gray-400"}`} />
                                        <span className="truncate">
                                            <b>{s.site_code || s.label}</b>{s.city ? ` · ${s.city}` : ""}
                                            {s.location ? <span className="text-gray-400"> · Lager {s.location}</span> : null}
                                            {s.is_home ? <span className="text-[10px] text-gray-400"> (mein Standort)</span> : null}
                                        </span>
                                    </span>
                                    <b className={s.stock_qty > 0 ? "text-emerald-600" : "text-gray-400"}>{s.stock_qty}</b>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>

            {scanOpen && <EanCameraScanner onDetected={onDetected} onClose={() => setScanOpen(false)} />}
        </div>
    );
}
